import { useEffect, useMemo } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import useAPI from '../api';
import { sectionLayoutsAtom } from '../Atoms';
import {
  DIALECTS,
  readStored,
  readableAs,
  migrateRegions,
} from '../Components/engine/dialect';

/**
 * The row a set of saved layouts applies to a surface: a sub-section's own layout first, then the
 * one marked default.
 *
 * @param {Array} layouts - Rows as the API returns them.
 * @param {Number} subSectionId - The sub-section in scope, if any.
 *
 * @returns {Object}
 */
export const pickSavedLayout = (layouts, subSectionId = null) => {
  const candidates = (layouts ?? []).filter((layout) =>
    subSectionId
      ? layout?.subSectionId === subSectionId || !layout?.subSectionId
      : !layout?.subSectionId
  );

  return (
    candidates.find((layout) => layout?.subSectionId === subSectionId) ??
    candidates.find((layout) => layout?.isDefault) ??
    candidates[0] ??
    null
  );
};

/**
 * A saved row's layout, or null when it holds nothing usable.
 *
 * `allowEmpty` keeps a saved layout with no entries, for a surface where showing nothing is a
 * choice rather than a missing configuration.
 *
 * @param {Object} row - A saved layout row.
 * @param {Object} options - Parsing options.
 * @param {Boolean} options.allowEmpty - Whether an empty layout is meaningful.
 *
 * @returns {Array}
 */
export const parseSavedLayout = (row, { allowEmpty = false } = {}) => {
  if (!row?.jsonStructure) return null;

  const held = (() => {
    try {
      return readStored(JSON.parse(row.jsonStructure));
    } catch {
      console.warn(`Layout ${row.id} of ${row.sectionName} is not valid JSON`);

      return null;
    }
  })();

  // A document that does not state its dialect is not one this build knows how to read, so the
  // surface falls back to what is checked in rather than guessing at it.
  if (!held) {
    console.warn(
      `Layout ${row.id} of ${row.sectionName} states no dialect and was not read`
    );

    return null;
  }

  const { newer, from } = readableAs(DIALECTS.LAYOUT, held.dialect);

  // Drawn as it stands rather than withheld: a surface that refuses to appear says nothing about
  // why, where one drawn by an older reader shows what it is.
  if (newer)
    console.warn(
      `Layout ${row.id} of ${row.sectionName} was saved in dialect ${from}, which this build does not know`
    );

  const layout = newer ? held.layout : migrateRegions(held.layout, from);

  return layout.length > 0 || allowEmpty ? layout : null;
};

// What has been asked of the server, as `section|token`. Marked before the request rather than
// after it, so several surfaces mounting together ask once, and a reload token asks again when it
// changes rather than merely because it exists. Held beside the atom rather than in it: it says what
// has been asked, which is not part of the answer.
//
// Keyed by the token as well as the section because readers of one section carry tokens of their
// own - a list bumps one on save, a detail page has none - and a key holding only the section had
// them cancelling each other's answers, each fetch making the other look out of date.
const asked = new Set();

/** What has been asked of a section, under whatever tokens. */
const askedOf = (sectionName) =>
  [...asked].filter((key) => key.startsWith(`${sectionName}|`));

/**
 * Every saved layout of a section, fetched once for all its surfaces.
 *
 * A section is asked for in a single call - the endpoint returns all surfaces when it is not given
 * one - so a page that reads its list, filters, sorting and search layouts makes one request, and a
 * surface opened later, like a customer's detail page, renders from what is already held rather than
 * waiting for a call of its own.
 *
 * @param {String} sectionName - The section whose layouts are wanted.
 * @param {*} reloadToken - Changing it refetches, for after the editor saves.
 *
 * @returns {Array} The rows, or null while none have arrived.
 */
export const useSectionLayouts = (sectionName, reloadToken = null) => {
  const API = useAPI();
  const [sections, setSections] = useAtom(sectionLayoutsAtom);

  const held = sections[sectionName];

  // Asked for when the section is not held, or when the token says what is held is out of date.
  // Watching only the name meant an invalidated section stayed dropped until something else
  // remounted the hook, and every reader fell back to the layout checked in.
  useEffect(() => {
    if (!sectionName) return;

    const key = `${sectionName}|${reloadToken ?? ''}`;

    if (asked.has(key)) return;

    asked.add(key);

    const settle = (rows) =>
      setSections((current) => ({ ...current, [sectionName]: rows }));

    API.getSectionLayouts(
      sectionName,
      null,
      { silent: true },
      (res) => settle(res ?? []),
      () => settle([])
    );
  }, [sectionName, reloadToken, held]);

  return held ?? null;
};

/**
 * Drops what is held for a section, so the next read fetches it again.
 *
 * For after the editor saves: the surfaces reading a layout are not the editor's children - the
 * customer overlay is mounted by the router, not by the page - so they are told through what they
 * read rather than through a prop.
 *
 * @returns {Function} Receives the section name.
 */
export const useInvalidateSectionLayouts = () => {
  const setSections = useSetAtom(sectionLayoutsAtom);

  return (sectionName) => {
    askedOf(sectionName).forEach((key) => asked.delete(key));
    setSections((current) =>
      Object.fromEntries(
        Object.entries(current).filter(([held]) => held !== sectionName)
      )
    );
  };
};

/**
 * The saved layout of a section's surface, falling back to the one checked in.
 *
 * The request is silent and its failure is not surfaced: a section with nothing saved, or a user
 * without the permission to read layouts, renders the checked-in default rather than an error.
 *
 * @param {String} sectionName - The section whose layout is wanted.
 * @param {String} surface - list, detail, filters or form.
 * @param {Array} defaultLayout - Used until a saved one arrives, and if none does.
 * @param {Number} subSectionId - Prefers a layout saved for this sub-section.
 * @param {*} reloadToken - Changing it refetches, for after the editor saves.
 * @param {Object} options - Reading options.
 * @param {Boolean} options.allowEmpty - Whether a saved layout with no entries is meaningful.
 *
 * @returns {Array}
 */
const useSectionLayout = (
  sectionName,
  surface,
  defaultLayout,
  subSectionId = null,
  reloadToken = null,
  { allowEmpty = false } = {}
) => {
  const rows = useSectionLayouts(sectionName, reloadToken);

  // The checked-in default is a layout document like a saved one, so it is read the same way and
  // what this hands back is the content either of them holds.
  const checkedIn = useMemo(() => {
    const held = readStored(defaultLayout);

    if (!held) return defaultLayout ?? null;

    return migrateRegions(held.layout, held.dialect);
  }, [defaultLayout]);

  // Held across renders: what comes back is parsed JSON, and handing back a new array each render
  // would rebuild every cell compiled from it and re-render everything drawing them.
  return useMemo(() => {
    if (!rows) return checkedIn;

    const forSurface = rows.filter(
      (row) =>
        String(row?.surface).toLowerCase() === String(surface).toLowerCase()
    );

    return (
      parseSavedLayout(pickSavedLayout(forSurface, subSectionId), {
        allowEmpty,
      }) ?? checkedIn
    );
  }, [rows, surface, subSectionId, allowEmpty, checkedIn]);
};

export default useSectionLayout;
